// Phase 3B — First Value + Return Loop.
//
// Pure, dependency-free model for the behavioural sequence:
//   VISIT → EXPERIENCE → REMEMBER → RETURN → IDENTIFY → OWN
//
// Nothing here touches the DOM, storage, or the network, so every rule is
// testable. Phase 3A (journey.ts / funnel.ts) is untouched and still owns
// LISTEN → ENTER → OWN; this layer sits inside it and describes what happens
// between a first experience and a return.

export type ReturnStage = "visit" | "experience" | "remember" | "return" | "identify" | "own";

/** Where a visitor currently sits in the return loop. */
export type ReturnState =
  | "first-visit"        // arrived, nothing experienced yet
  | "in-experience"      // engaged with real content this session
  | "value-received"     // completed at least one meaningful experience
  | "returning"          // came back on a later day
  | "identified"         // has an account
  | "owner";             // member or purchaser

export const RETURN_STAGE_FOR_STATE: Record<ReturnState, ReturnStage> = {
  "first-visit": "visit",
  "in-experience": "experience",
  "value-received": "remember",
  returning: "return",
  identified: "identify",
  owner: "own",
};

// -- Meaningful actions -------------------------------------------------------
//
// "First value" is only claimed for actions that require real engagement with
// real content. Pageviews, hovers, scroll depth and CTA clicks are excluded on
// purpose: they are intent, not value.

/** Actions that count as *starting* an experience. */
export const EXPERIENCE_START_EVENTS = [
  "player_play",
  "release_play",
  "activation_start",
  "activation_play",
  "chapter_open",
  "lesson_start",
  "gate_engage",
  "first_gate_breath",
] as const;

/** Actions that count as *receiving value* — a completed experience. */
export const VALUE_EVENTS = [
  "activation_complete",
  "chapter_complete",
  "lesson_complete",
  "gate_complete",
  "daily_gate_click",
  "wisdom_save",
  "continue_reading_click",
] as const;

export type MeaningfulKind = "start" | "value";

export function meaningfulKind(event: string): MeaningfulKind | null {
  if ((EXPERIENCE_START_EVENTS as readonly string[]).includes(event)) return "start";
  if ((VALUE_EVENTS as readonly string[]).includes(event)) return "value";
  return null;
}

export function isMeaningfulAction(event: string): boolean {
  return meaningfulKind(event) !== null;
}

// -- State resolution ---------------------------------------------------------

export interface ReturnSignals {
  signedIn: boolean;
  isMember: boolean;
  /** Distinct calendar days the visitor has been seen, including today. */
  visitDays: number;
  /** Count of experience-start actions ever recorded on this device. */
  startedCount: number;
  /** Count of completed-value actions ever recorded on this device. */
  valueCount: number;
}

export function resolveReturnState(s: ReturnSignals): ReturnState {
  if (s.isMember) return "owner";
  if (s.signedIn) return "identified";
  if (s.visitDays > 1) return "returning";
  if (s.valueCount > 0) return "value-received";
  if (s.startedCount > 0) return "in-experience";
  return "first-visit";
}

// -- Path memory --------------------------------------------------------------

export type MemoryKind = "music" | "wisdom" | "activation" | "gate" | "book" | "academy";

export interface PathMemoryEntry {
  kind: MemoryKind;
  /** Stable content reference (slug, gate number as string, lesson path). */
  ref: string;
  title: string;
  href: string;
  /** Epoch ms of the last interaction. */
  at: number;
  /** 0–1 when the surface can report progress. */
  progress?: number;
  completed?: boolean;
}

export const MEMORY_MAX = 20;
/** A memory older than this stops being offered as a continuation. */
export const MEMORY_TTL_DAYS = 45;
const DAY = 86_400_000;

export function isStale(entry: PathMemoryEntry, now = Date.now()): boolean {
  return now - entry.at > MEMORY_TTL_DAYS * DAY;
}

/** Newest-first, de-duplicated by kind+ref, bounded. */
export function mergeMemory(
  list: PathMemoryEntry[],
  entry: PathMemoryEntry,
): PathMemoryEntry[] {
  const prev = list.find((e) => e.kind === entry.kind && e.ref === entry.ref);
  const merged: PathMemoryEntry = prev
    ? {
        ...prev,
        ...entry,
        progress: Math.max(prev.progress ?? 0, entry.progress ?? 0) || undefined,
        completed: Boolean(prev.completed || entry.completed),
      }
    : entry;
  const rest = list.filter((e) => !(e.kind === entry.kind && e.ref === entry.ref));
  return [merged, ...rest].slice(0, MEMORY_MAX);
}

/**
 * The single thing to offer as "continue". Prefers an unfinished experience,
 * then the most recent one. Never offers the page the visitor is reading,
 * never offers a stale or completed-only memory.
 */
export function pickContinuation(
  list: PathMemoryEntry[],
  opts: { now?: number; currentHref?: string } = {},
): PathMemoryEntry | null {
  const now = opts.now ?? Date.now();
  const fresh = list
    .filter((e) => !isStale(e, now))
    .filter((e) => e.href !== opts.currentHref)
    .sort((a, b) => b.at - a.at);
  return fresh.find((e) => !e.completed) ?? null;
}

// -- Conversion prompt discipline ---------------------------------------------
//
// A prompt is a request. It is only earned after value has been given, and it
// is capped hard so returning visitors are never worn down.

export const PROMPT_MIN_VALUE_ACTIONS = 1;
export const PROMPT_MIN_STARTS = 2;
export const PROMPT_COOLDOWN_DAYS = 7;

export interface PromptSignals {
  state: ReturnState;
  startedCount: number;
  valueCount: number;
  /** Epoch ms of the last time any conversion prompt was shown. */
  lastPromptAt: number | null;
  /** Epoch ms of the last explicit dismissal. */
  lastDismissedAt: number | null;
  /** True when a prompt has already been shown in this session. */
  shownThisSession: boolean;
  now?: number;
}

export type PromptDecision =
  | { show: false; reason: string }
  | { show: true; ask: "account" | "membership" };

export function conversionPromptDecision(s: PromptSignals): PromptDecision {
  const now = s.now ?? Date.now();
  if (s.state === "owner") return { show: false, reason: "already_owner" };
  if (s.shownThisSession) return { show: false, reason: "session_cap" };
  if (s.valueCount < PROMPT_MIN_VALUE_ACTIONS && s.startedCount < PROMPT_MIN_STARTS) {
    return { show: false, reason: "value_not_yet_given" };
  }
  const cooldown = PROMPT_COOLDOWN_DAYS * DAY;
  if (s.lastDismissedAt && now - s.lastDismissedAt < cooldown) {
    return { show: false, reason: "dismissal_cooldown" };
  }
  if (s.lastPromptAt && now - s.lastPromptAt < cooldown) {
    return { show: false, reason: "prompt_cooldown" };
  }
  return { show: true, ask: s.state === "identified" ? "membership" : "account" };
}

// -- Measurement definitions --------------------------------------------------
//
// Honest labelling: a metric is either measured from first-party events, a
// declared proxy, or explicitly unavailable. Proxies are never presented as
// the real thing, and nothing here implies causation.

export type MetricAvailability = "measured" | "proxy" | "unavailable";

export interface ReturnMetricDef {
  id: string;
  label: string;
  stage: ReturnStage;
  availability: MetricAvailability;
  /** Event names counted, when measured or proxied. */
  events: string[];
  note: string;
}

export const RETURN_METRICS: ReturnMetricDef[] = [
  {
    id: "first_experience",
    label: "First experience started",
    stage: "experience",
    availability: "measured",
    events: [...EXPERIENCE_START_EVENTS],
    note: "Sessions with at least one real content interaction.",
  },
  {
    id: "value_received",
    label: "Experience completed",
    stage: "remember",
    availability: "measured",
    events: [...VALUE_EVENTS],
    note: "Completion signals reported by the content surfaces themselves.",
  },
  {
    id: "memory_offered",
    label: "Continuation offered",
    stage: "remember",
    availability: "measured",
    events: ["return_continue_view"],
    note: "Times a stored continuation was surfaced.",
  },
  {
    id: "memory_resumed",
    label: "Continuation resumed",
    stage: "return",
    availability: "measured",
    events: ["return_continue_click"],
    note: "Times a visitor picked their path back up.",
  },
  {
    id: "return_visit",
    label: "Return visit",
    stage: "return",
    availability: "proxy",
    events: ["journey_returning_visit", "return_visit_day"],
    note: "Device-local flag. Cleared storage or a new device reads as a new visitor, so this undercounts.",
  },
  {
    id: "identify",
    label: "Account created",
    stage: "identify",
    availability: "measured",
    events: ["auth_signup", "newsletter_submit"],
    note: "Completed identification events.",
  },
  {
    id: "own",
    label: "Membership or purchase",
    stage: "own",
    availability: "measured",
    events: ["purchase", "membership_start", "checkout_complete"],
    note: "Completed commercial conversions.",
  },
  {
    id: "cohort_retention",
    label: "Per-visitor cohort retention",
    stage: "return",
    availability: "unavailable",
    events: [],
    note: "Not measurable: analytics events are session-scoped with no durable visitor identifier, and none will be added.",
  },
  {
    id: "value_attribution",
    label: "Revenue attributed to first value",
    stage: "own",
    availability: "unavailable",
    events: [],
    note: "Not measurable: no per-visitor linkage between an experience and a later purchase. Correlation would not be causation.",
  },
];

export interface ReturnMetricResult extends ReturnMetricDef {
  events_count: number;
  sessions_count: number;
}

export function computeReturnMetrics(
  rows: Array<{ event: string; session_id: string | null }>,
): ReturnMetricResult[] {
  return RETURN_METRICS.map((m) => {
    const sessions = new Set<string>();
    let count = 0;
    for (const r of rows) {
      if (!m.events.includes(r.event)) continue;
      count += 1;
      if (r.session_id) sessions.add(r.session_id);
    }
    return { ...m, events_count: count, sessions_count: sessions.size };
  });
}

/** Resume rate = continuations clicked ÷ continuations shown. Null when unshown. */
export function resumeRate(metrics: ReturnMetricResult[]): number | null {
  const shown = metrics.find((m) => m.id === "memory_offered")?.events_count ?? 0;
  const clicked = metrics.find((m) => m.id === "memory_resumed")?.events_count ?? 0;
  return shown > 0 ? clicked / shown : null;
}
